// Display job progress
import { useEffect, useState } from 'react';

// This component shows progress items for a job (scrape or analysis)
// It limits to displaying only the most recent 10 items as specified
const JobProgress = ({ jobId, jobType, progressItems = [], isCompleted = false }) => {
  // State to track if the progress section is expanded
  const [isExpanded, setIsExpanded] = useState(!isCompleted);
  
  // Use only the 10 most recent items
  const recentItems = progressItems.slice(-10);

  // Format the timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  // Status styles
  const getStatusColor = () => {
    if (isCompleted) return 'bg-green-500';
    return 'bg-blue-500';
  };

  const getStatusText = () => {
    if (isCompleted) return 'Completed';
    return 'In Progress';
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 mt-4">
      <div 
        className="flex items-center justify-between cursor-pointer" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <h3 className="text-lg font-medium text-gray-900 mr-2">Job Progress</h3>
          <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
        <button className="text-gray-500">
          {isExpanded ? (
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4">
          {recentItems.length === 0 ? (
            <p className="text-gray-500 text-sm">No progress information available yet.</p>
          ) : (
            <div className="space-y-4">
              {/* Timeline display */}
              <div className="flow-root">
                <ul className="-mb-8">
                  {recentItems.map((item, index) => (
                    <li key={item.progressId || index}>
                      <div className="relative pb-8">
                        {index !== recentItems.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          ></span>
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center ring-8 ring-white">
                              <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-800">{item.text}</p>
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              {formatTime(item.timestamp)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* Show note about limited progress items */}
          {progressItems.length > 10 && (
            <p className="text-xs text-gray-500 mt-2">
              Showing 10 most recent updates out of {progressItems.length} total updates.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default JobProgress;